import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Activity, ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, Camera, Clock3, Download, ExternalLink, ImageOff, PauseCircle, PlayCircle, RefreshCw, Search, Users, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useUser } from "./Context/UserContext.tsx";
import "./CSS/AdminDashboard.css";

interface EmployeeActivity {
  employeeId?: number | null;
  empName?: string | null;
  email?: string | null;
  ss?: string | null;
  todayTotalTime?: string | null;
  thisweektime?: string | null;
  thismonth?: string | null;
  online?: boolean | null; // The API field holds the last record's IsPaused flag.
  latestActivityAt?: string | null;
}
type Filter = "all" | "tracking" | "paused" | "no-activity";
type SortKey = "name" | "today" | "week" | "month";
const seconds = (value?: string | null) => {
  const match = String(value || "").match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?/);
  return match ? Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] || 0) : 0;
};
const duration = (value: number) => {
  const minutes = Math.floor(value / 60);
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
};
const display = (value?: string | null) => value == null ? "—" : duration(seconds(value));
const stateOf = (person: EmployeeActivity): Exclude<Filter, "all"> =>
  !person.todayTotalTime && !person.latestActivityAt ? "no-activity" : person.online === true ? "paused" : "tracking";
const stateLabel = { tracking: "Tracking", paused: "Paused", "no-activity": "No activity today" };
const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const AdminDashboardList: React.FC = () => {
  const { user } = useUser();
  const adminId = user?.id;
  const [data, setData] = useState<EmployeeActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("today");
  const [ascending, setAscending] = useState(false);
  const [preview, setPreview] = useState<EmployeeActivity | null>(null);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    if (!adminId) {
      setLoading(false);
      setError("Your manager account could not be identified. Please sign in again.");
      return;
    }
    setRefreshing(true);
    try {
      const response = await axios.get<EmployeeActivity[]>(
        `https://timetrakerapi.sntservices.in/AdminEmployeeLastActivity?Adminid=${adminId}`, { signal }
      );
      if (signal?.aborted) return;
      setData(Array.isArray(response.data) ? response.data : []);
      setUpdatedAt(new Date());
      setError("");
    } catch (err) {
      if (signal?.aborted || axios.isCancel(err)) return;
      setError("Activity data could not be loaded. Check your connection and try again.");
    } finally {
      if (!signal?.aborted) { setLoading(false); setRefreshing(false); }
    }
  }, [adminId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    const timer = window.setInterval(() => fetchData(controller.signal), 30000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [fetchData]);
  useEffect(() => {
    if (!preview) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [preview]);

  const stats = useMemo(() => ({
    today: data.reduce((sum, item) => sum + seconds(item.todayTotalTime), 0),
    week: data.reduce((sum, item) => sum + seconds(item.thisweektime), 0),
    month: data.reduce((sum, item) => sum + seconds(item.thismonth), 0),
    tracking: data.filter((item) => stateOf(item) === "tracking").length,
    paused: data.filter((item) => stateOf(item) === "paused").length,
    noActivity: data.filter((item) => stateOf(item) === "no-activity").length,
  }), [data]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const key = sortKey === "today" ? "todayTotalTime" : sortKey === "week" ? "thisweektime" : "thismonth";
    return data.filter((item) =>
      (filter === "all" || stateOf(item) === filter) &&
      (!needle || `${item.empName || ""} ${item.email || ""}`.toLowerCase().includes(needle))
    ).sort((a, b) => {
      const diff = sortKey === "name"
        ? (a.empName || a.email || "").localeCompare(b.empName || b.email || "")
        : seconds(a[key]) - seconds(b[key]);
      return (ascending ? 1 : -1) * diff;
    });
  }, [data, query, filter, sortKey, ascending]);

  const changeSort = (key: SortKey) => {
    if (key === sortKey) setAscending(!ascending);
    else { setSortKey(key); setAscending(key === "name"); }
  };
  const sortIcon = (key: SortKey) => sortKey !== key ? <ArrowUpDown size={14} /> : ascending ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  const exportCsv = () => {
    const rows = [
      ["Employee", "Email", "Last recorded state", "Latest activity today", "Today", "This week", "This month"],
      ...visible.map((item) => [item.empName || "", item.email || "", stateLabel[stateOf(item)],
        item.latestActivityAt ? new Date(item.latestActivityAt).toLocaleString() : "",
        item.todayTotalTime || "", item.thisweektime || "", item.thismonth || ""]),
    ];
    const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `team-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const todayLabel = new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }).format(new Date());

  return <main className="activity-dashboard">
    <header className="activity-header">
      <div><div className="activity-eyebrow"><span className="activity-live-dot" /> TEAM OVERVIEW <span>/</span> {todayLabel}</div><h1>Activity dashboard</h1><p>Recorded time and recent activity across your team.</p></div>
      <div className="activity-header-actions">
        <button className="activity-btn activity-btn-secondary" type="button" onClick={() => fetchData()} disabled={refreshing}><RefreshCw size={16} className={refreshing ? "activity-spinning" : ""} /> Refresh</button>
        <button className="activity-btn activity-btn-primary" type="button" onClick={exportCsv} disabled={loading || !visible.length}><Download size={16} /> Export CSV</button>
      </div>
    </header>
    {error && <div className="activity-alert" role="alert">{error} <button type="button" onClick={() => fetchData()}>Retry</button></div>}
    <section className="activity-metrics" aria-label="Team summary">
      <div className="activity-metric activity-metric-featured"><div className="activity-metric-top">Total tracked today <span className="activity-metric-icon"><Clock3 size={19} /></span></div><strong>{loading ? "—" : duration(stats.today)}</strong><small>{data.length - stats.noActivity} team members with time today</small></div>
      <div className="activity-metric"><div className="activity-metric-top">Team members <span className="activity-metric-icon"><Users size={19} /></span></div><strong>{loading ? "—" : data.length}</strong><small>{data.length - stats.noActivity} recorded time today</small></div>
      <div className="activity-metric"><div className="activity-metric-top">Last state: tracking <span className="activity-metric-icon activity-icon-green"><Activity size={19} /></span></div><strong>{loading ? "—" : stats.tracking}</strong><small>{stats.paused} paused · {stats.noActivity} no activity today</small></div>
      <div className="activity-metric"><div className="activity-metric-top">This week <span className="activity-metric-icon"><CalendarDays size={19} /></span></div><strong>{loading ? "—" : duration(stats.week)}</strong><small>{duration(stats.month)} recorded this month</small></div>
    </section>
    <section className="activity-panel" aria-labelledby="employee-breakdown">
      <div className="activity-panel-heading"><div><span className="activity-section-kicker">TEAM ACTIVITY</span><h2 id="employee-breakdown">Employee breakdown <span>{data.length}</span></h2><p>Today, this week, and this month at a glance.</p></div><Link className="activity-timeline-link" to="/admin-dashboard/timeline">View timeline <ExternalLink size={15} /></Link></div>
      <div className="activity-toolbar">
        <label className="activity-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search employee or email" aria-label="Search employee or email" /></label>
        <div className="activity-filters" role="group" aria-label="Filter by last recorded state">{(["all", "tracking", "paused", "no-activity"] as Filter[]).map((option) => <button key={option} type="button" className={filter === option ? "is-active" : ""} aria-pressed={filter === option} onClick={() => setFilter(option)}>{option === "all" ? "All" : option === "no-activity" ? "No activity" : stateLabel[option]}</button>)}</div>
      </div>
      <div className="activity-table-wrap"><table className="activity-table"><thead><tr>
        <th><button type="button" onClick={() => changeSort("name")}>Employee {sortIcon("name")}</button></th><th>Last recorded state</th>
        <th><button type="button" onClick={() => changeSort("today")}>Today {sortIcon("today")}</button></th>
        <th><button type="button" onClick={() => changeSort("week")}>This week {sortIcon("week")}</button></th>
        <th><button type="button" onClick={() => changeSort("month")}>This month {sortIcon("month")}</button></th><th>Latest screenshot today</th>
      </tr></thead><tbody>
        {loading ? <tr><td colSpan={6} className="activity-empty"><span className="activity-loader" /> Loading team activity…</td></tr> : visible.length === 0 ? <tr><td colSpan={6} className="activity-empty">{data.length ? "No employees match your search or filter." : "No employees have been assigned to this manager yet."}</td></tr> : visible.map((person, index) => {
          const state = stateOf(person);
          const name = person.empName?.trim() || "Name unavailable";
          const initials = name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
          const lastTime = person.latestActivityAt ? new Date(person.latestActivityAt) : null;
          return <tr key={person.employeeId ?? person.email ?? `${name}-${index}`}>
            <td><div className="activity-person"><span className="activity-avatar">{initials}</span><span className="activity-person-copy">{person.email ? <Link className="activity-person-link" to={`/admin-dashboard/timeline?email=${encodeURIComponent(person.email)}&name=${encodeURIComponent(name)}`}>{name} <ExternalLink size={12} /></Link> : <strong>{name}</strong>}<small>{person.email || "Email unavailable"}</small></span></div></td>
            <td><span className={`activity-status activity-status-${state}`}>{state === "tracking" ? <PlayCircle size={15} /> : state === "paused" ? <PauseCircle size={15} /> : <Clock3 size={15} />}{stateLabel[state]}</span>{lastTime && !Number.isNaN(lastTime.valueOf()) && <small className="activity-last-time">Last entry {lastTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</small>}</td>
            <td><strong className="activity-time-primary">{display(person.todayTotalTime)}</strong>{person.todayTotalTime && stats.today > 0 && <div className="activity-progress" title={`${Math.round(seconds(person.todayTotalTime) / stats.today * 100)}% of team time today`}><span style={{ width: `${Math.min(100, seconds(person.todayTotalTime) / stats.today * 100)}%` }} /></div>}</td>
            <td className="activity-time">{display(person.thisweektime)}</td><td className="activity-time">{display(person.thismonth)}</td>
            <td>{person.ss ? <button type="button" className="activity-shot-button" onClick={() => setPreview(person)} aria-label={`View latest screenshot for ${name}`}><img src={person.ss} alt="" loading="lazy" /><span><Camera size={15} /> View</span></button> : <span className="activity-no-shot"><ImageOff size={16} /> No screenshot</span>}</td>
          </tr>;
        })}
      </tbody></table></div>
      <div className="activity-panel-footer"><span>Showing {visible.length} of {data.length} employees</span><span>{updatedAt ? `Updated ${updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · Refreshes every 30 seconds` : "Waiting for latest data"}</span></div>
    </section>
    <p className="activity-footnote">Status reflects the latest time entry today; it does not confirm live online presence. Weekly totals start Sunday.</p>
    {preview?.ss && <div className="activity-modal" role="presentation" onMouseDown={() => setPreview(null)}><div className="activity-modal-dialog" role="dialog" aria-modal="true" aria-label={`Latest screenshot for ${preview.empName || preview.email || "employee"}`} onMouseDown={(event) => event.stopPropagation()}><div className="activity-modal-header"><div><strong>{preview.empName || preview.email}</strong><span>Latest screenshot today</span></div><button type="button" onClick={() => setPreview(null)} aria-label="Close screenshot"><X size={20} /></button></div><img src={preview.ss} alt={`Latest screenshot for ${preview.empName || preview.email}`} /></div></div>}
  </main>;
};

export default AdminDashboardList;
